import { useEffect, useState } from 'react';
import {
  highlightCode,
  type HighlightedLines,
  type HighlightTheme,
  type ThemedToken,
} from '../../lib/syntax-highlight';

/**
 * Tracks the active app theme by watching the `dark` class on `<html>`, so
 * Shiki tokens are highlighted with a palette readable on the current
 * background. Shared by the read-only {@link CommandBlock} and the editable
 * {@link EditableCommandList} so both render commands identically.
 */
export function useDocumentTheme(): HighlightTheme {
  const [theme, setTheme] = useState<HighlightTheme>(() => {
    if (typeof document === 'undefined') return 'dark';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return theme;
}

/**
 * Highlights a list of shell commands as bash, returning per-command token
 * lines (or `null` for a command that has not highlighted yet / failed).
 * Re-highlights when the command set or theme changes.
 */
export function useHighlightedCommands(commands: string[], theme: HighlightTheme) {
  const [tokensByCommand, setTokensByCommand] = useState<(HighlightedLines | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    Promise.all(commands.map((cmd) => highlightCode(cmd, 'command.sh', theme, 'bash'))).then(
      (results) => {
        if (!cancelled) setTokensByCommand(results);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [commands.join('\x00'), theme]);

  return tokensByCommand;
}

/** Renders pre-tokenized highlighted code as colored spans. */
export function HighlightedCode({ tokens }: { tokens: HighlightedLines }) {
  return (
    <>
      {tokens.map((line, i) => (
        <span key={i}>
          {line.map((token: ThemedToken, j: number) => (
            <span key={j} style={{ color: token.color }}>
              {token.content}
            </span>
          ))}
          {i < tokens.length - 1 && '\n'}
        </span>
      ))}
    </>
  );
}
